import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UseUnsavedChangesProps {
  isDirty: boolean;
  onSave: () => Promise<void> | void;
  onDiscard: () => void;
}

export const useUnsavedChanges = ({
  isDirty,
  onSave,
  onDiscard,
}: UseUnsavedChangesProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open && isDirty) {
      setShowConfirmDialog(true);
      return false;
    }
    return true;
  };

  const handleDiscard = () => {
    setShowConfirmDialog(false);
    setPendingClose(true);
    onDiscard();
  };

  const handleSave = async () => {
    setShowConfirmDialog(false);
    await onSave();
    setPendingClose(true);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  const ConfirmDialog = () => (
    <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. What would you like to do?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDiscard}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Discard Changes
          </AlertDialogAction>
          <AlertDialogAction onClick={handleSave}>
            Save Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return {
    handleOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  };
};
